import { t } from "@lingui/macro";
import { useMemo, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  ImageIcon,
  ListPlusIcon,
  PlusIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Separator,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { toast } from "sonner";

import { usePreviewEmail, type EmailBlocks } from "@/client/services/campaigns/campaigns";
import { useUploadImage } from "@/client/services/storage/upload-image";

/** Poids maximal d'une image de campagne (apres reduction serveur). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ÉDITEUR D'E-MAIL DE CAMPAGNE — blocs structurés mail-safe
 * ─────────────────────────────────────────────────────────────────────────────
 *  Aucun HTML brut : chaque bloc (hero, intro, sections, CTAs, note) est
 *  rendu côté serveur dans le template de marque → aperçu fidèle (iframe
 *  chargée depuis POST /campaigns/preview-email).
 *  Variables : {{name}}, {{firstName}}, {{email}}.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const VARIABLE_BUTTONS = [
  { label: "{{name}}", hint: "Nom complet du destinataire" },
  { label: "{{firstName}}", hint: "Prénom (1er mot du nom)" },
  { label: "{{email}}", hint: "Adresse e-mail" },
] as const;

type BlocksEditorProps = {
  subject: string;
  onSubjectChange: (value: string) => void;
  preheader: string;
  onPreheaderChange: (value: string) => void;
  blocks: EmailBlocks;
  onBlocksChange: (blocks: EmailBlocks) => void;
  fallbackBody: string;
  link: string;
  title: string;
};

export const EmailBlocksEditor = ({
  subject,
  onSubjectChange,
  preheader,
  onPreheaderChange,
  blocks,
  onBlocksChange,
  fallbackBody,
  link,
  title,
}: BlocksEditorProps) => {
  const preview = usePreviewEmail();
  const upload = useUploadImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");

  /**
   * Téléverse une image et met à jour le bloc héros de la campagne.
   * Le stockage renvoie l'URL publique conservée en base ; le serveur la
   * convertit en route publique au moment du rendu de l'e-mail.
   */
  const handleImageFile = async (file: File | undefined | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Le fichier sélectionné n'est pas une image");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image trop volumineuse : 5 Mo maximum");
      return;
    }

    try {
      const response = await upload.uploadImage(file);
      onBlocksChange({ ...blocks, heroImageUrl: response.data });
      toast.success("Image ajoutée à la campagne");
    } catch {
      toast.error("Impossible de téléverser l'image");
    }
  };

  const hasRichContent = useMemo(
    () => Boolean(blocks.intro || (blocks.sections?.length ?? 0) > 0 || blocks.heroImageUrl),
    [blocks],
  );

  const updateSection = (index: number, patch: Partial<{ heading: string; body: string }>) => {
    const sections = [...(blocks.sections ?? [])];
    sections[index] = { ...sections[index], ...patch };
    onBlocksChange({ ...blocks, sections });
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    const sections = [...(blocks.sections ?? [])];
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    [sections[index], sections[target]] = [sections[target], sections[index]];
    onBlocksChange({ ...blocks, sections });
  };

  const openPreview = async () => {
    try {
      const result = await preview.mutateAsync({
        title: title || subject || "Votre campagne",
        body: fallbackBody || undefined,
        link: link || undefined,
        emailSubject: subject || undefined,
        emailBlocks: hasRichContent ? blocks : undefined,
      });
      setPreviewHtml(result.html);
    } catch {
      toast.error("Impossible de générer l'aperçu e-mail");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Objet + préheader */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-admin-text-secondary">Objet de l'e-mail</label>
          <span
            className={cn(
              "text-xs",
              subject.length > 60 ? "text-amber-600" : "text-admin-text-muted",
            )}
          >
            {subject.length}/60 recommandé
          </span>
        </div>
        <Input
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="Défaut : titre de la campagne"
          className="border-admin-border bg-admin-surface-elevated"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-admin-text-secondary">
          Préheader (aperçu boîte de réception)
        </label>
        <Input
          value={preheader}
          onChange={(e) => onPreheaderChange(e.target.value)}
          placeholder="Complète l'objet dans la liste des e-mails — 90 caractères max"
          className="border-admin-border bg-admin-surface-elevated"
        />
      </div>

      {/* Variables de personnalisation */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl-admin border border-admin-border bg-admin-surface-elevated p-2">
        <span className="px-1 text-xs font-medium text-admin-text-muted">Personnalisation :</span>
        {VARIABLE_BUTTONS.map((v) => (
          <span
            key={v.label}
            title={v.hint}
            className="cursor-help rounded-full bg-admin-primary/10 px-2 py-0.5 font-mono text-xs text-admin-primary"
          >
            {v.label}
          </span>
        ))}
      </div>

      {/* Image d'en-tête : téléversement ou URL */}
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-sm font-medium text-admin-text-secondary">
          <ImageIcon size={14} /> Image d'en-tête
        </label>

        {blocks.heroImageUrl ? (
          <div className="flex items-center gap-3 rounded-lg border border-admin-border bg-admin-surface-elevated p-2">
            <img
              src={blocks.heroImageUrl}
              alt="Aperçu de l'image d'en-tête"
              className="h-16 w-24 shrink-0 rounded-md border border-admin-border object-cover"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="truncate text-xs text-admin-text-muted">{blocks.heroImageUrl}</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={upload.loading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {upload.loading ? "Téléversement..." : "Remplacer"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onBlocksChange({ ...blocks, heroImageUrl: undefined })}
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={upload.loading}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              void handleImageFile(event.dataTransfer.files?.[0]);
            }}
            className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-admin-border bg-admin-surface-elevated px-4 py-6 text-center transition-colors hover:border-admin-primary hover:bg-admin-surface"
          >
            <UploadSimpleIcon size={20} className="text-admin-text-muted" />
            <span className="text-sm text-admin-text-secondary">
              {upload.loading
                ? "Téléversement de l'image..."
                : "Déposez une image ici ou cliquez pour parcourir"}
            </span>
            <span className="text-xs text-admin-text-muted">{t`JPG (.jpg), PNG (.png) ou WebP (.webp), 5 Mo maximum`}</span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            void handleImageFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        <div className="flex flex-col gap-1">
          <span className="text-xs text-admin-text-muted">
            Ou adresse d'une image déjà en ligne
          </span>
          <Input
            value={blocks.heroImageUrl ?? ""}
            onChange={(e) =>
              onBlocksChange({ ...blocks, heroImageUrl: e.target.value || undefined })
            }
            placeholder="https://..."
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
      </div>

      {/* Accroche */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-admin-text-secondary">
          Accroche d'ouverture
        </label>
        <textarea
          value={blocks.intro ?? ""}
          onChange={(e) => onBlocksChange({ ...blocks, intro: e.target.value || undefined })}
          placeholder="Bonjour {{firstName}}, une nouvelle qui devrait vous plaire…"
          rows={2}
          className="w-full rounded-md border border-admin-border bg-admin-surface-elevated p-2 text-sm text-admin-text-primary outline-none placeholder:text-admin-text-muted focus:ring-1 focus:ring-admin-primary"
        />
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-1.5 text-sm font-medium text-admin-text-secondary">
            <ListPlusIcon size={14} /> Sections du message
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onBlocksChange({
                ...blocks,
                sections: [...(blocks.sections ?? []), { heading: "", body: "" }],
              })
            }
            className="gap-1.5"
          >
            <PlusIcon size={12} /> Ajouter une section
          </Button>
        </div>

        {(blocks.sections ?? []).map((section, index) => (
          <div
            key={index}
            className="flex flex-col gap-2 rounded-lg border border-admin-border p-3"
          >
            <div className="flex items-center gap-2">
              <Input
                value={section.heading ?? ""}
                onChange={(e) => updateSection(index, { heading: e.target.value })}
                placeholder={`Titre de la section ${index + 1}`}
                className="border-admin-border bg-admin-surface-elevated text-sm font-semibold"
              />
              <div className="flex shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveSection(index, -1)}
                  title="Monter"
                >
                  <ArrowUpIcon size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveSection(index, 1)}
                  title="Descendre"
                >
                  <ArrowDownIcon size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    onBlocksChange({
                      ...blocks,
                      sections: (blocks.sections ?? []).filter((_, i) => i !== index),
                    })
                  }
                  title="Supprimer"
                >
                  <TrashIcon size={14} />
                </Button>
              </div>
            </div>
            <textarea
              value={section.body ?? ""}
              onChange={(e) => updateSection(index, { body: e.target.value })}
              placeholder="Paragraphe… Les sauts de ligne sont conservés. Variables {{name}} acceptées."
              rows={3}
              className="w-full rounded-md border border-admin-border bg-admin-surface-elevated p-2 text-sm text-admin-text-primary outline-none placeholder:text-admin-text-muted focus:ring-1 focus:ring-admin-primary"
            />
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">
            Bouton principal — libellé
          </label>
          <Input
            value={blocks.ctaLabel ?? ""}
            onChange={(e) => onBlocksChange({ ...blocks, ctaLabel: e.target.value || undefined })}
            placeholder="Ex. Créer mon CV maintenant"
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">
            Bouton principal — URL
          </label>
          <Input
            value={blocks.ctaUrl ?? ""}
            onChange={(e) => onBlocksChange({ ...blocks, ctaUrl: e.target.value || undefined })}
            placeholder={`Défaut : lien de la campagne${link ? ` (${link})` : ""}`}
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">
            Lien secondaire — libellé
          </label>
          <Input
            value={blocks.secondaryCtaLabel ?? ""}
            onChange={(e) =>
              onBlocksChange({ ...blocks, secondaryCtaLabel: e.target.value || undefined })
            }
            placeholder="Ex. Découvrir les templates"
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">
            Lien secondaire — URL
          </label>
          <Input
            value={blocks.secondaryCtaUrl ?? ""}
            onChange={(e) =>
              onBlocksChange({ ...blocks, secondaryCtaUrl: e.target.value || undefined })
            }
            placeholder="https://…"
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
      </div>

      {/* Note de confiance */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-admin-text-secondary">
          Note de confiance (footer)
        </label>
        <Input
          value={blocks.note ?? ""}
          onChange={(e) => onBlocksChange({ ...blocks, note: e.target.value || undefined })}
          placeholder="Ex. Votre CV reste votre propriété — aucune donnée revendue."
          className="border-admin-border bg-admin-surface-elevated"
        />
      </div>

      {!hasRichContent && (
        <p className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-2.5 text-xs text-sky-600">
          Aucun bloc riche : l'e-mail utilisera le message simple (onglet Contenu). Complétez
          l'accroche ou une section pour activer le rendu riche.
        </p>
      )}

      {/* Aperçu */}
      <Separator className="bg-admin-border" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-admin-text-muted">
          L'aperçu utilise le moteur de rendu réel du serveur (identique à l'envoi).
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={openPreview}
          disabled={preview.isPending}
          className="gap-1.5"
        >
          {preview.isPending ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <EyeIcon size={14} />
          )}
          Aperçu e-mail
        </Button>
      </div>

      {/* Dialog d'aperçu */}
      <Dialog open={previewHtml !== null} onOpenChange={(open) => !open && setPreviewHtml(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-admin-border bg-admin-surface">
          <DialogHeader>
            <DialogTitle className="text-admin-text-primary">Aperçu de l'e-mail</DialogTitle>
            <DialogDescription className="text-admin-text-muted">
              Rendu réel — personnalisation de démonstration (« Amadou Diallo »).
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-2">
            <Button
              variant={previewMode === "desktop" ? "brand" : "outline"}
              size="sm"
              onClick={() => setPreviewMode("desktop")}
            >
              Ordinateur
            </Button>
            <Button
              variant={previewMode === "mobile" ? "brand" : "outline"}
              size="sm"
              onClick={() => setPreviewMode("mobile")}
            >
              Mobile
            </Button>
          </div>
          <div className="flex justify-center overflow-hidden rounded-xl border border-admin-border bg-white">
            <iframe
              title="Aperçu e-mail"
              srcDoc={previewHtml ?? ""}
              sandbox=""
              className="h-[70vh] bg-white"
              style={{ width: previewMode === "desktop" ? "640px" : "375px" }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
